import React, { useLayoutEffect, useRef, useState } from 'react';
import { TagPicker } from 'rsuite';

// Width kept free for the "N more ..." label when the tags don't all fit.
const MORE_LABEL_WIDTH = 96;
// The toggle's own padding plus the caret, which the tags cannot occupy.
const TOGGLE_CHROME_WIDTH = 60;

export default function CustomMultiSelect({
    data = [],
    form = null,
    name = "",
    overFlowVisibleOptions = 1,
    menuClassName = "",
    onScrollToBottom = null
}) {
    const wrapperRef = useRef(null);
    const tagsRef = useRef(null);
    const [hiddenCount, setHiddenCount] = useState(0);
    const selectedValues = form?.values[name];
    // Callers build `data` inline, so it is a new array on every render — keying the
    // measure effect on it would re-run (and re-flicker) constantly. These two
    // primitives change only when the tags themselves actually change.
    const selectionKey = Array.isArray(selectedValues) ? selectedValues.join(',') : '';
    const dataCount = data.length;

    // Show as many tags as the field is actually wide enough for, rather than a
    // fixed count: measure them, hide the ones that overflow, count the rest.
    // overFlowVisibleOptions is the minimum kept visible however narrow the field.
    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        let frame = null;
        let lastWidth = -1;

        const measure = () => {
            const container = tagsRef.current;
            if (!container) return;
            const tags = Array.from(container.querySelectorAll('.cms-tag'));
            if (!tags.length) {
                setHiddenCount(0);
                return;
            }
            tags.forEach(tag => { tag.style.display = ''; });

            // Measured against the wrapper, not the tag row: the tag row sits inside
            // rsuite's toggle and sizes to its content, so hiding tags would shrink it
            // and the next measure would show them again — an endless flicker.
            const available = wrapper.clientWidth - TOGGLE_CHROME_WIDTH;
            lastWidth = wrapper.clientWidth;
            const widths = tags.map(tag => tag.offsetWidth + 4); // 4px inter-tag gap
            const total = widths.reduce((sum, width) => sum + width, 0);

            let visible = tags.length;
            if (total > available) {
                const budget = available - MORE_LABEL_WIDTH;
                let used = 0;
                visible = 0;
                for (const width of widths) {
                    if (used + width > budget) break;
                    used += width;
                    visible++;
                }
                visible = Math.max(visible, overFlowVisibleOptions);
            }

            tags.forEach((tag, i) => { tag.style.display = i < visible ? '' : 'none'; });
            setHiddenCount(tags.length - visible);
        };

        measure();

        // Watch the wrapper, so only a real layout change re-measures. Deferred to the
        // next frame: mutating layout inside the callback is what makes the browser
        // report "ResizeObserver loop completed with undelivered notifications".
        const observer = new ResizeObserver(entries => {
            const width = entries[0]?.contentRect?.width ?? 0;
            if (Math.abs(width - lastWidth) < 1) return;
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        });
        observer.observe(wrapper);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [selectionKey, dataCount, overFlowVisibleOptions]);

    // Trigger load-more when the menu is scrolled near the bottom (infinite scroll).
    // The listener goes on rsuite's own scrolling list, which already carries
    // overflow-y: auto — wrapping it in a second scroll box here would put two
    // scrollbars side by side in the menu. TagPicker builds its list from
    // CheckPicker, so the element is .rs-picker-check-menu-items; the select
    // variant and the plain role=listbox are kept as fallbacks.
    const detachMenuScroll = useRef(null);
    const attachMenuScroll = (node) => {
        if (detachMenuScroll.current) {
            detachMenuScroll.current();
            detachMenuScroll.current = null;
        }
        if (!node || !onScrollToBottom) return;
        const list = node.querySelector('.rs-picker-check-menu-items, .rs-picker-select-menu-items, [role="listbox"]');
        if (!list) return;
        const handler = () => {
            const { scrollTop, scrollHeight, clientHeight } = list;
            if (scrollHeight - scrollTop - clientHeight <= 20) {
                onScrollToBottom();
            }
        };
        list.addEventListener('scroll', handler);
        detachMenuScroll.current = () => list.removeEventListener('scroll', handler);
    };
    const selectAllChecked = data.length === form?.values[name]?.length;

    // Render the popup inside the nearest scrollable ancestor (e.g. a modal body)
    // so it scrolls together with the input instead of jumping up/down while the modal scrolls.
    const getMenuContainer = () => {
        let node = wrapperRef.current?.parentElement;
        while (node && node !== document.body) {
            const { overflowY } = window.getComputedStyle(node);
            if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
                return node;
            }
            node = node.parentElement;
        }
        return document.body;
    };

    // Handle Select All toggle
    const handleSelectAll = () => {
        if (selectAllChecked) {
            form.setFieldValue(name, []);
        } else {
            form.setFieldValue(name, data.map(record => record?.value));
        }
    };

    return (
        <div ref={wrapperRef}>
        <TagPicker
            container={getMenuContainer}
            menuClassName={menuClassName}
            data={data} // We are only passing the actual data, not "Select All" in this list
            defaultValue={form?.values[name]}
            className='form-control form-control-select'
            renderMenu={menu => {
                return (
                    <div ref={attachMenuScroll}>
                        <div className={"rs-picker-list-item cp"}>
                            <div className={"rs-check-item rs-checkbox e_none" + (selectAllChecked ? " rs-checkbox-checked" : "")} onClick={handleSelectAll}>
                                <div className="rs-checkbox-checker">
                                    <label htmlFor='select-all'>
                                        <span className="rs-checkbox-control">
                                            <input type="checkbox" id='select-all' checked={selectAllChecked} readOnly />
                                            <span className="rs-checkbox-inner"></span>
                                        </span>
                                        <span className="rs-checkbox-label">Select All</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        {menu}
                    </div>
                );
            }}
            renderValue={(value, items) => {
                const removeTag = (i) => {
                    let allValues = [...form?.values[name]];
                    allValues.splice(i, 1);
                    form.setFieldValue(name, allValues);
                };
                // Every tag is rendered so it can be measured; the ones that don't
                // fit are hidden by the layout effect above.
                return <span ref={tagsRef} className='d-flex align-items-center' style={{ overflow: 'hidden' }}>
                    {items.map((item, i) => {
                        return (
                            <span className="rs-tag-text cms-tag" key={i}>
                                <div role="option" title="" className="rs-tag rs-tag-md rs-tag-default rs-tag-closable">
                                    <span className="rs-tag-text">{item?.label}</span>
                                    <button type="button" className="rs-tag-icon-close rs-btn-close" aria-label="Remove" onClick={() => { removeTag(i) }}>
                                        <svg width="1em" height="1em" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false" className="rs-icon" aria-label="close" data-category="application"><path d="M2.784 2.089l.069.058 5.146 5.147 5.146-5.147a.5.5 0 01.765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 01-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 01-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 01.638-.765z"></path></svg>
                                    </button>
                                </div>
                            </span>
                        )
                    })}
                    {hiddenCount > 0 ? <span className='ms-2 text-nowrap'>{hiddenCount} more ...</span> : null}
                </span>
            }}
            cleanable={false}
            value={form?.values[name]}
            onChange={(e) => {
                form.setFieldValue(name, e);
            }}
            placeholder='Please select'
        />
        </div>
    );
}
